import { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Select from 'react-select';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { CircularProgress, Fade, Tooltip, Chip, LinearProgress, Dialog } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ClaimsManager from './ClaimsManager';
import RolesManager from './RolesManager';

const UserDetail = () => {
    // Session validation
    useSessionValidation();
    
    const { id } = useParams();
    const history = useHistory();

    const { isAuthenticated, companyId, token, logout } = useAuth();
    const [mounted, setMounted] = useState(false);
    
    useEffect(() => {
        setMounted(true);
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }

    }, [isAuthenticated, companyId]);

    const [isSavingRoles, setIsSavingRoles] = useState(false);
    const [isSavingClaims, setIsSavingClaims] = useState(false);
    const [selectedOption, setSelectedOption] = useState({ value: '1', label: 'Master' });

    const [isEditMode, setIsEditMode] = useState(false);
    const [isClaimEditMode, setIsClaimEditMode] = useState(false);

    const [formData, setFormData] = useState({
        FirstName: '',
        LastName: '',
        UserName: '',
        Email: '',
        ContactNumber: '',
        isSubmit: false,
        Roles: [],
    });

    const [formDataClaim, setFormDataClaim] = useState({
        FirstName: '',
        LastName: '',
        UserName: '',
        Email: '',
        ContactNumber: '',
        isSubmit: false,
        Claim: [],
    });

    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        fetchData('UserApi', `/UserEdit/${id}`, token) //, token
            .then((response) => {
                if (response.responseCode === '1111') {
                    const { User } = response.response_data;
                    setFormData((prevFormData) => ({
                        ...prevFormData,
                        ...User,
                        Roles: response.response_data.Roles ? response.response_data.Roles.map((role) => ({ ...role })) : [],
                    }));
                    setFormDataClaim((prevFormDataClaim) => ({
                        ...prevFormDataClaim,
                        ...User,
                        Claim: response.response_data.Claims
                            ? response.response_data.Claims.map((claim) => ({ ...claim }))
                            : [],
                    }));
                }
            })
            .catch((error) => {
                console.error('Error fetching User Details:', error);
            });
    }, [id]);

    const handleChangeRole = (index) => {
        const updatedRoles = formData.Roles.map((role, i) => ({
            ...role,
            IsSelected: i === index ? !role.IsSelected : false,
        }));
        setFormData((prevFormData) => ({ ...prevFormData, Roles: updatedRoles }));
    };

    const getClaimRange = () => {
        const startIndex = (parseInt(selectedOption.value, 10) - 1) * 5;
        const endIndex = startIndex + 4;
        return { startIndex, endIndex };
    };

    const handleDropdownChange = (newSelectedOption) => {
        setSelectedOption(newSelectedOption);
    };

    const handleChange = (index) => {
        const updatedClaims = [...formDataClaim.Claim];
        updatedClaims[index].IsSelected = !updatedClaims[index].IsSelected;
        setFormDataClaim((prevFormDataClaim) => ({ ...prevFormDataClaim, Claim: updatedClaims }));
    };

    const handleManageRolesClick = () => {
        setIsEditMode(true);
    };

    const saveRoles = async (updatedRoles) => {
        try {
            setIsSavingRoles(true);
            const updatedFormData = {
                ...formData,
                Roles: updatedRoles || formData.Roles
            };
            const response = await updateData('UserApi', `SaveUserRoles/tmp/${id}`, updatedFormData, token);

            if (response.responseCode === '1111') {
                setSuccessMessage('Roles saved successfully');
                setFormData(updatedFormData);
            } else {
                setErrorMessage('Failed to save roles');
            }
            setIsEditMode(false);

        } catch (error) {
            console.error('Error saving roles:', error);
            setErrorMessage('Error saving roles');
        }
        finally {
            setIsSavingRoles(false);
        }
    };

    const handleCancelRolesEdit = () => {
        setIsEditMode(false);
    };

    const handleManageClaimsClick = () => {
        setIsClaimEditMode(true);
    };

    const saveClaims = async (updatedClaims) => {
        try {
            setIsSavingClaims(true);
            const updatedFormData = {
                ...formDataClaim,
                Claim: updatedClaims || formDataClaim.Claim
            };
            const response = await updateData('UserApi', `SaveUserClaims/UpdateClaim/tmp/${id}`, updatedFormData, token);

            if (response.responseCode === '1111') {
                setSuccessMessage('Claims saved successfully');
                setFormDataClaim(updatedFormData);
            } else {
                setErrorMessage('Failed to save claims');
            }
            setIsClaimEditMode(false);

        } catch (error) {
            console.error('Error saving Claim:', error);
            setErrorMessage('Error saving claims');
        }
        finally {
            setIsSavingClaims(false);
        }
    };

    const handleCancelClaimsEdit = () => {
        setIsClaimEditMode(false);
    };

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '0',
                position: 'relative',
                marginTop: '-20px'
            }}>
                <style>{`
                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .input-field-detail {
                        transition: all 0.3s ease;
                        border: 2px solid transparent;
                    }
                    .input-field-detail:hover {
                        border-color: #e0e0e0;
                    }
                    .action-button-detail {
                        transition: all 0.3s ease;
                        text-transform: none;
                        font-weight: 600;
                        letter-spacing: 0.5px;
                    }
                    .action-button-detail:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
                    }
                    .section-card-detail {
                        animation: slideIn 0.6s ease-out forwards;
                        opacity: 0;
                    }
                    .section-card-detail:nth-child(1) { animation-delay: 0.1s; }
                    .section-card-detail:nth-child(2) { animation-delay: 0.2s; }
                    .section-card-detail:nth-child(3) { animation-delay: 0.3s; }
                    @keyframes shimmer {
                        0% { background-position: -1000px 0; }
                        100% { background-position: 1000px 0; }
                    }
                    .shimmer {
                        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
                        background-size: 1000px 100%;
                        animation: shimmer 2s infinite;
                    }
                `}</style>

                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '8px 12px',
                    position: 'relative'
                }}>
                    {/* Header Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px',
                        paddingBottom: '4px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                padding: '6px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)'
                            }}>
                                <PersonIcon style={{ fontSize: '18px', color: 'white' }} />
                            </div>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <h1 style={{
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        margin: 0,
                                        letterSpacing: '-0.3px'
                                    }}>
                                        User Details
                                    </h1>
                                    <Tooltip title="View and manage user information" arrow placement="right">
                                        <InfoIcon style={{ fontSize: '14px', color: '#667eea', cursor: 'pointer' }} />
                                    </Tooltip>
                                </div>
                                <p style={{ margin: 0, color: '#666', fontSize: '10px', marginTop: '2px' }}>
                                    View user information, roles, and claims
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <Tooltip title="Return to user list" arrow>
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to="/UserIndex"
                                    startIcon={<ArrowBackIcon />}
                                    className="action-button-detail"
                                    style={{
                                        borderColor: '#667eea',
                                        color: '#667eea',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        borderWidth: '1.5px',
                                        minHeight: '28px'
                                    }}
                                >
                                    Back to List
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Error/Success Messages */}
                    {errorMessage && (
                        <div style={{
                            padding: '10px',
                            marginBottom: '10px',
                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <InfoIcon style={{ fontSize: '18px' }} />
                            {errorMessage}
                        </div>
                    )}
                    {successMessage && (
                        <div style={{
                            padding: '10px',
                            marginBottom: '10px',
                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <CheckCircleIcon style={{ fontSize: '18px' }} />
                            {successMessage}
                        </div>
                    )}

                    {/* User Information Section */}
                    <div className="section-card-detail" style={{
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                        padding: '10px',
                        borderRadius: '10px',
                        marginBottom: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            background: 'rgba(102, 126, 234, 0.9)',
                            borderRadius: '50%',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <CheckCircleIcon style={{ fontSize: '16px', color: 'white' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <PersonIcon style={{ color: '#667eea', fontSize: '18px' }} />
                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                                Personal Information
                            </h3>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    <PersonIcon style={{ fontSize: '13px' }} />
                                    First Name
                                </label>
                                <input
                                    className="input-field-detail"
                                    value={formData.FirstName}
                                    name='FirstName'
                                    type="text"
                                    placeholder="First Name"
                                    readOnly
                                    style={{
                                        width: '100%',
                                        padding: '6px 10px',
                                        fontSize: '13px',
                                        borderRadius: '8px',
                                        border: '1.5px solid #667eea',
                                        background: '#fafafa',
                                        outline: 'none',
                                        fontWeight: '500',
                                        cursor: 'not-allowed'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    <PersonIcon style={{ fontSize: '13px' }} />
                                    Last Name
                                </label>
                                <input
                                    className="input-field-detail"
                                    value={formData.LastName}
                                    name='LastName'
                                    type="text"
                                    placeholder="Last Name"
                                    readOnly
                                    style={{
                                        width: '100%',
                                        padding: '6px 10px',
                                        fontSize: '13px',
                                        borderRadius: '8px',
                                        border: '1.5px solid #667eea',
                                        background: '#fafafa',
                                        outline: 'none',
                                        fontWeight: '500',
                                        cursor: 'not-allowed'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    <AccountCircleIcon style={{ fontSize: '13px' }} />
                                    User Name
                                </label>
                                <input
                                    className="input-field-detail"
                                    value={formData.UserName}
                                    name='UserName'
                                    type="text"
                                    placeholder="User Name"
                                    readOnly
                                    style={{
                                        width: '100%',
                                        padding: '6px 10px',
                                        fontSize: '13px',
                                        borderRadius: '8px',
                                        border: '1.5px solid #667eea',
                                        background: '#fafafa',
                                        outline: 'none',
                                        fontWeight: '500',
                                        cursor: 'not-allowed'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    <PhoneIcon style={{ fontSize: '13px' }} />
                                    Contact Number
                                </label>
                                <input
                                    className="input-field-detail"
                                    value={formData.ContactNumber}
                                    name='ContactNumber'
                                    type="text"
                                    placeholder="Contact Number"
                                    readOnly
                                    style={{
                                        width: '100%',
                                        padding: '6px 10px',
                                        fontSize: '13px',
                                        borderRadius: '8px',
                                        border: '1.5px solid #667eea',
                                        background: '#fafafa',
                                        outline: 'none',
                                        fontWeight: '500',
                                        cursor: 'not-allowed'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    <EmailIcon style={{ fontSize: '13px' }} />
                                    Email Address
                                </label>
                                <input
                                    className="input-field-detail"
                                    value={formData.Email}
                                    name='Email'
                                    type="text"
                                    placeholder="Email"
                                    readOnly
                                    style={{
                                        width: '100%',
                                        padding: '6px 10px',
                                        fontSize: '13px',
                                        borderRadius: '8px',
                                        border: '1.5px solid #667eea',
                                        background: '#fafafa',
                                        outline: 'none',
                                        fontWeight: '500',
                                        cursor: 'not-allowed'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Claims Manager Dialog */}
                    <Dialog
                        open={isClaimEditMode}
                        onClose={handleCancelClaimsEdit}
                        maxWidth="md"
                        fullWidth
                        PaperProps={{
                            style: {
                                borderRadius: '16px',
                                maxHeight: '90vh'
                            }
                        }}
                    >
                        <ClaimsManager
                            claims={formDataClaim.Claim || []}
                            onSave={saveClaims}
                            onCancel={handleCancelClaimsEdit}
                            isSaving={isSavingClaims}
                        />
                    </Dialog>

                    {/* Roles Manager Dialog */}
                    <Dialog
                        open={isEditMode}
                        onClose={handleCancelRolesEdit}
                        maxWidth="md"
                        fullWidth
                        PaperProps={{
                            style: {
                                borderRadius: '16px',
                                maxHeight: '90vh'
                            }
                        }}
                    >
                        <RolesManager
                            roles={formData.Roles || []}
                            onSave={saveRoles}
                            onCancel={handleCancelRolesEdit}
                            isSaving={isSavingRoles}
                        />
                    </Dialog>

                    {/* Roles and Claims Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '10px' }}>
                        {/* User Claims Summary */}
                        <div className="section-card-detail" style={{
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                            padding: '10px',
                            borderRadius: '10px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '10px',
                                paddingBottom: '8px',
                                borderBottom: '1px solid rgba(102, 126, 234, 0.2)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <SecurityIcon style={{ color: '#667eea', fontSize: '18px' }} />
                                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                                        User Claims
                                    </h3>
                                </div>
                                <Tooltip title="Manage user claims" arrow>
                                    <Button
                                        onClick={handleManageClaimsClick}
                                        variant="contained"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        className="action-button-detail"
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)',
                                            minHeight: '28px'
                                        }}
                                    >
                                        Manage Claims
                                    </Button>
                                </Tooltip>
                            </div>
                            
                            {/* Claims Statistics */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '6px',
                                marginBottom: '10px'
                            }}>
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    padding: '6px 10px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}>
                                    <div style={{ fontSize: '18px', fontWeight: '700' }}>
                                        {formDataClaim.Claim ? formDataClaim.Claim.length : 0}
                                    </div>
                                    <div style={{ fontSize: '10px', opacity: 0.9, fontWeight: '500' }}>Total Claims</div>
                                </div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                    padding: '6px 10px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}>
                                    <div style={{ fontSize: '18px', fontWeight: '700' }}>
                                        {formDataClaim.Claim ? formDataClaim.Claim.filter(c => c.IsSelected).length : 0}
                                    </div>
                                    <div style={{ fontSize: '10px', opacity: 0.9, fontWeight: '500' }}>Active Claims</div>
                                </div>
                            </div>

                            {/* Active Claims Preview */}
                            <div style={{
                                maxHeight: '300px',
                                overflowY: 'auto',
                                padding: '8px',
                                background: 'white',
                                borderRadius: '8px'
                            }}>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '8px',
                                    padding: '0 4px'
                                }}>
                                    Active Claims:
                                </div>
                                {formDataClaim.Claim && formDataClaim.Claim.length > 0 ? (
                                    formDataClaim.Claim.filter(c => c.IsSelected).length > 0 ? (
                                        formDataClaim.Claim.filter(c => c.IsSelected).map((claim, index) => (
                                            <div key={index} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '6px 8px',
                                                marginBottom: '4px',
                                                background: 'linear-gradient(135deg, rgba(17, 153, 142, 0.1) 0%, rgba(56, 239, 125, 0.1) 100%)',
                                                borderRadius: '4px',
                                                border: '1px solid #11998e'
                                            }}>
                                                <CheckCircleIcon style={{ fontSize: '14px', color: '#11998e' }} />
                                                <span style={{
                                                    fontSize: '11px',
                                                    fontWeight: '500',
                                                    color: '#11998e',
                                                    flex: 1
                                                }}>
                                                    {claim.ClaimValue}
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        <p style={{ fontSize: '11px', color: '#666', textAlign: 'center', padding: '15px', margin: 0 }}>
                                            No active claims. Click "Manage Claims" to assign permissions.
                                        </p>
                                    )
                                ) : (
                                    <p style={{ fontSize: '11px', color: '#666', textAlign: 'center', padding: '15px', margin: 0 }}>
                                        Loading claims...
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* User Role Summary */}
                        <div className="section-card-detail" style={{
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                            padding: '10px',
                            borderRadius: '10px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '10px',
                                paddingBottom: '8px',
                                borderBottom: '1px solid rgba(102, 126, 234, 0.2)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <AdminPanelSettingsIcon style={{ color: '#667eea', fontSize: '18px' }} />
                                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                                        User Roles
                                    </h3>
                                </div>
                                <Tooltip title="Manage user roles" arrow>
                                    <Button
                                        onClick={handleManageRolesClick}
                                        variant="contained"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        className="action-button-detail"
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)',
                                            minHeight: '28px'
                                        }}
                                    >
                                        Manage Roles
                                    </Button>
                                </Tooltip>
                            </div>
                            
                            {/* Roles Statistics */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '6px',
                                marginBottom: '10px'
                            }}>
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    padding: '6px 10px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}>
                                    <div style={{ fontSize: '18px', fontWeight: '700' }}>
                                        {formData.Roles ? formData.Roles.length : 0}
                                    </div>
                                    <div style={{ fontSize: '10px', opacity: 0.9, fontWeight: '500' }}>Total Roles</div>
                                </div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                    padding: '6px 10px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}>
                                    <div style={{ fontSize: '18px', fontWeight: '700' }}>
                                        {formData.Roles ? formData.Roles.filter(r => r.IsSelected).length : 0}
                                    </div>
                                    <div style={{ fontSize: '10px', opacity: 0.9, fontWeight: '500' }}>Active Role</div>
                                </div>
                            </div>

                            {/* Active Role Preview */}
                            <div style={{
                                maxHeight: '300px',
                                overflowY: 'auto',
                                padding: '8px',
                                background: 'white',
                                borderRadius: '8px'
                            }}>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '8px',
                                    padding: '0 4px'
                                }}>
                                    Active Role:
                                </div>
                                {formData.Roles && formData.Roles.filter(r => r.IsSelected).length > 0 ? (
                                    formData.Roles.filter(r => r.IsSelected).map((role, index) => (
                                        <div key={index} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '6px 8px',
                                            marginBottom: '4px',
                                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                                            borderRadius: '4px',
                                            border: '1px solid #667eea'
                                        }}>
                                            <CheckCircleIcon style={{ fontSize: '14px', color: '#667eea' }} />
                                            <span style={{
                                                fontSize: '11px',
                                                fontWeight: '500',
                                                color: '#667eea',
                                                flex: 1
                                            }}>
                                                {role.RoleName}
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <p style={{ fontSize: '11px', color: '#666', textAlign: 'center', padding: '15px', margin: 0 }}>
                                        No active role. Click "Manage Roles" to assign a role.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    fontFamily: 'serif',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '13px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    zIndex: 1000
                }}>
                    <CopyrightIcon style={{ fontSize: '18px', color: '#667eea' }} />
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>
            </div>
        </Fade>
    );
};

export default UserDetail;
